import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useStyles } from './SectionDetail.styles';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { Icons } from '../../../../icons';
import { btnSize, btnType, Button } from '../../../atoms/button';
import { CurrentSection } from './components';
import { MEMBER_ACTIONS, Sections, subsections } from '../sessions.constants';
import { PatchAction } from '../../../../../redux/modules/appointment/types';
import useDebounce from '../../../../../hooks/useDebounce';
import { patchAppointment } from '../../../../../services/appointment/appointment.service';
import Giggle from '../../../../../pages/provider/sessions/giggle';
import { convertSecondToTime } from '../../../../../utils/CommonUtils';
import { colors } from '../../../../colors';

const SectionDetail = ({
  section,
  subSection,
  idFront,
  idBack,
  nextAppointment,
  nextGeneralAppointment,
  onEndSession,
  showKeyboardShortcuts,
  goToSchedule,
  appointment,
  onRestartSessionClick,
  openEvaluationsDrawer,
  jumpToOpenSession,
  onAddEvaluation,
  listHelper,
  exerciseHelper,
  currentEvaluation,
  currentBlock,
  selectedIndex,
  sessionEnded,
  setTextInputInFocus,
  dctPreviousResponses,
  dctSelectedResponses,
  onPreviousResponseSelected,
  onNext,
  scratchPadText,
  onPrevious,
  onMemberActionClick,
  evaluationsCompleted,
  leftTrigger,
  rightTrigger,
  previousResponses,
  useAllPreviousResponses,
  onSkipEvaluation,
  onRejectEvaluation,
  treatmentPlan,
  modalities,
  profileElements,
  expandedEvaluation,
  jumpToEvaluation,
  handleJumpToEvaluation,
  scrollToTop,
  setScrollToTop,
  aiMode,
  setAiMode,
  onScratchpadUpdate,
}) => {
  const bodyRef = useRef(null);
  const giggleKeywords = ['giggle', 'laugh', 'chuckle'];
  const getRandomKeyword = () => giggleKeywords[Math.floor(Math.random() * giggleKeywords.length)];
  const [promptsVisible, setPromptsVisible] = useState(true);
  const [open, setOpen] = useState(false);
  const classes = useStyles({
    withBanner: section === Sections.OPEN_SESSION,
    withJumpToDoc: section === Sections.OPEN_SESSION,
    evaluationsList: section === Sections.EVALUATIONS,
  });
  const hasNextAppointment =
    nextGeneralAppointment && dayjs(nextGeneralAppointment.startTime).isSame(dayjs(), 'day');
  const [timerValue, setTimerValue] = useState(
    hasNextAppointment ? dayjs(nextGeneralAppointment.startTime).diff(dayjs(), 'second') : 0,
  );
  const [scratchpad, setScratchpad] = useState(scratchPadText ?? '');
  const scratchpadNotes = useDebounce(scratchpad, 1500);

  const addScratchpadNotes = notes => {
    setScratchpad(notes);
    // Notify parent component of scratchpad update
    if (onScratchpadUpdate) {
      onScratchpadUpdate({ scratchpad: notes, addScratchpadNotes });
    }
  };

  // Send initial scratchpad value to parent when scratchPadText changes
  useEffect(() => {
    if (onScratchpadUpdate && scratchPadText !== undefined) {
      onScratchpadUpdate({ scratchpad: scratchPadText, addScratchpadNotes });
    }
  }, [scratchPadText]); // Only depend on scratchPadText, not onScratchpadUpdate

  useEffect(() => {
    if (!nextGeneralAppointment || timerValue <= 0) return;

    const intervalId = setInterval(() => {
      setTimerValue(prev => prev - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [nextGeneralAppointment, timerValue]);

  const saveScratchpadNotes = async () => {
    patchAppointment(
      { action: PatchAction.ADD_METAINFO, appointmentMetaInfoRequestDto: { scratchpad } },
      { appointmentId: appointment.appointmentId },
    );
  };

  useEffect(() => {
    setScratchpad(scratchPadText ?? '');
  }, [scratchPadText]);

  useEffect(() => {
    if (scratchpad && scratchpad !== '') void saveScratchpadNotes();
  }, [scratchpadNotes]);

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.mainContent} ref={bodyRef}>
        <CurrentSection
          selectedSection={section}
          selectedSubsection={subSection}
          promptsVisible={promptsVisible}
          idFront={idFront}
          idBack={idBack}
          eligibleForAutodoc={appointment?.sessionAnalysisAllowed}
          dctPreviousResponses={dctPreviousResponses}
          dctSelectedResponses={dctSelectedResponses}
          onPreviousResponseSelected={onPreviousResponseSelected}
          scratchpad={scratchpad}
          addScratchpadNotes={addScratchpadNotes}
          openEvaluationsDrawer={openEvaluationsDrawer}
          onAddEvaluation={onAddEvaluation}
          currentEvaluation={currentEvaluation}
          currentBlock={currentBlock}
          listHelper={listHelper}
          exerciseHelper={exerciseHelper}
          profileElements={profileElements}
          evaluationsCompleted={evaluationsCompleted}
          setTextInputInFocus={setTextInputInFocus}
          onRejectEvaluation={onRejectEvaluation}
          bodyRef={bodyRef}
          selectedIndex={selectedIndex}
          leftTrigger={leftTrigger}
          rightTrigger={rightTrigger}
          nextAppointment={nextAppointment}
          nextGeneralAppointment={nextGeneralAppointment}
          previousResponses={previousResponses}
          appointment={appointment}
          useAllPreviousResponses={useAllPreviousResponses}
          treatmentPlan={treatmentPlan}
          modalities={modalities}
          expandedEvaluation={expandedEvaluation}
          scrollToTop={scrollToTop}
          setScrollToTop={setScrollToTop}
          aiMode={aiMode}
          setAiMode={setAiMode}
        />
      </Box>
      {section === Sections.OPEN_SESSION && (
        <Box display="flex" flexDirection="column">
          {jumpToEvaluation && (
            <Box className={classes.backToDocBanner}>
              <p className={classes.bannerText}>Back to the documentation</p>
              <Button
                variant={btnType.TEXT}
                className={classes.textLink}
                endIcon={<Icons glyph="square-arrow-up-right" />}
                onClick={() => handleJumpToEvaluation(jumpToEvaluation)}
              >
                Jump back
              </Button>
            </Box>
          )}
          <Box className={classes.bookAppointmentBanner}>
            <Box className={classes.nextApptContent}>
              <p className={classes.nextApptTitle}>Next appointment</p>
              <p className={clsx(classes.nextApptTime, { [classes.danger]: !nextAppointment })}>
                {nextAppointment
                  ? `Scheduled on ${dayjs(nextAppointment.startTime).format('MM/DD/YY - hh:mm a')}`
                  : 'Nothing scheduled'}
              </p>
            </Box>
              <Icons
                color={colors.green500}
                glyph="calendar-checked-outlined"
                onClick={() => onMemberActionClick(MEMBER_ACTIONS.SCHEDULE)}
                className={classes.apptBtn}
              />
          </Box>
        </Box>
      )}
      {section === Sections.EVALUATIONS && (
        <Box display="flex" flexDirection="column">
          {!currentEvaluation && (
            <>
              {evaluationsCompleted ? (
                <Box className={classes.backToDocBanner}>
                  <p className={classes.bannerText}>Want to pick your own?</p>
                  <Button variant={btnType.TEXT} className={classes.textLink} onClick={openEvaluationsDrawer}>
                    Add Evaluation
                  </Button>
                </Box>
              ) : (
                <Box className={classes.backToDocBanner}>
                  <p className={classes.bannerText}>Start with Open Session instead?</p>
                  <Button variant={btnType.TEXT} className={classes.textLink} onClick={jumpToOpenSession}>
                    Skip ahead
                  </Button>
                </Box>
              )}
            </>
          )}
          {currentEvaluation && (
            <Box className={classes.backToDocBanner}>
              <p className={classes.bannerText}>No longer relevant? Out of time?</p>
              <Button variant={btnType.TEXT} className={classes.textLink} onClick={onSkipEvaluation}>
                Skip {currentEvaluation.type === 'EVALUATION' ? 'evaluation' : 'exercise'}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {section === Sections.CLOSE_SESSION && (
        <>
          {hasNextAppointment ? (
            <Box className={classes.backToScheduleBanner}>
              <Box display="flex" flexDirection="column" gap="4px">
                <p className={classes.nextApptText}>
                  You have an appointment in{' '}
                  <span className={classes.nextApptRemainingTime}>{convertSecondToTime(timerValue)}</span>
                </p>
                <p className={classes.nextApptSubText}>
                  {nextGeneralAppointment.serviceName} at {dayjs(nextGeneralAppointment?.startTime).format('hh:mma')}
                </p>
              </Box>

              <Button
                variant={btnType.SECONDARY}
                size={btnSize.SMALL}
                className={classes.openScheduleButton}
                onClick={goToSchedule}
              >
                Open Schedule
              </Button>
            </Box>
          ) : (
            <Box className={classes.backToScheduleBanner}>
              <p className={classes.bannerText}>You have no more appointments today</p>
              <Button
                variant={btnType.SECONDARY}
                size={btnSize.SMALL}
                className={classes.openScheduleButton}
                onClick={goToSchedule}
              >
                Open Schedule
              </Button>
            </Box>
          )}
        </>
      )}

      <Giggle open={open} setOpen={setOpen} keyword={getRandomKeyword()} heading={"Let's giggle!"} />
    </Box>
  );
};

export { SectionDetail };
